import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SaveIcon from "@mui/icons-material/Save";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SettingsIcon from "@mui/icons-material/Settings";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  useGetServerCommonNameListQuery,
  useGetWorkflowsCopyQuery,
  useLazyGetScheduleDetailsQuery,
  useSaveScheduleDetailsMutation,
  useSaveTemplatesMutation,
} from "../../redux/api";

const MISFIRE_SIMPLE = [
  { value: "ON_MISFIRE_EXECUTE_AS_SOON_AS_POSSIBLE", label: "Execute as soon as possible" },
  { value: "ON_MISFIRE_DO_NOTHING", label: "Do nothing" },
];

const INTERVAL_UNITS = [
  { value: "SECOND", label: "Second" },
  { value: "MINUTE", label: "Minute" },
  { value: "HOUR", label: "Hour" },
  { value: "DAY", label: "Day" },
  { value: "WEEK", label: "Week" },
  { value: "MONTH", label: "Month" },
  { value: "YEAR", label: "Year" },
  { value: "ADVANCED", label: "Advanced" },
];

const DAY_OPTIONS = [
  { value: 1, label: "Sun" },
  { value: 2, label: "Mon" },
  { value: 3, label: "Tue" },
  { value: 4, label: "Wed" },
  { value: 5, label: "Thu" },
  { value: 6, label: "Fri" },
  { value: 7, label: "Sat" },
];

const RUN_SERVER_LABELS = {
  LBL_RUN_ON_ALL_ACTIVE_SERVER: "Run On All Active Servers",
  LBL_RUN_ON_ANY_SERVER: "Run On Any Available Server",
  LBL_RUN_ON_PRIMARY: "Run On Primary Server",
};

const runServerDisplayText = (value) => RUN_SERVER_LABELS[value] || value;

function getAdvancedValidationErrors(cronExprBuilder) {
  const secMinPattern = /^(\*|[0-5]?\d|[0-5]?\d-[0-5]?\d|[0-5]?\d\/[1-9]\d*|[0-5]?\d(?:,[0-5]?\d)+)$/i;
  const hourPattern = /^(\*|(?:[01]?\d|2[0-3])|(?:[01]?\d|2[0-3])-(?:[01]?\d|2[0-3])|(?:[01]?\d|2[0-3])\/[1-9]\d*|(?:[01]?\d|2[0-3])(?:,(?:[01]?\d|2[0-3]))+)$/i;
  const monthPattern = /^(\*|(?:1[0-2]|0?[1-9]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)(?:,(?:1[0-2]|0?[1-9]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC))*|(?:1[0-2]|0?[1-9]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)-(?:1[0-2]|0?[1-9]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)|(?:1[0-2]|0?[1-9]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\/[1-9]\d*)$/i;
  const dayOfMonthPattern = /^(\?|L|LW|\*|(?:3[01]|[12]?\d)|(?:3[01]|[12]?\d)-(?:3[01]|[12]?\d)|(?:3[01]|[12]?\d)\/[1-9]\d*|(?:3[01]|[12]?\d)(?:,(?:3[01]|[12]?\d))+)$/i;
  const dayOfWeekPattern = /^(\?|L|\*|(?:[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT)(?:L|#[1-5])?|(?:[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT)(?:,(?:[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT))*|(?:[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT)-(?:[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT)|(?:[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT)\/[1-9]\d*)$/i;
  const yearPattern = /^$|^(\*|(?:19[7-9]\d|20\d\d)(?:,(?:19[7-9]\d|20\d\d))*|(?:19[7-9]\d|20\d\d)-(?:19[7-9]\d|20\d\d)|(?:19[7-9]\d|20\d\d)\/[1-9]\d*)$/;

  const seconds = String(cronExprBuilder?.seconds || "").trim();
  const minutes = String(cronExprBuilder?.minutes || "").trim();
  const hours = String(cronExprBuilder?.hours || "").trim();
  const month = String(cronExprBuilder?.month || "").trim();
  const dayOfMonth = String(cronExprBuilder?.dayOfMonth || "").trim();
  const dayOfWeek = String(cronExprBuilder?.dayOfWeek || "").trim();
  const year = String(cronExprBuilder?.year || "").trim();

  if (!secMinPattern.test(seconds)) return ["ERR_INVALID_ADV_PARAM_OPT"];
  if (!secMinPattern.test(minutes)) return ["ERR_INVALID_ADV_PARAM_OPT"];
  if (!hourPattern.test(hours)) return ["ERR_INVALID_ADV_PARAM_OPT"];
  if (!monthPattern.test(month)) return ["ERR_INVALID_ADV_PARAM_OPT"];
  if (!dayOfMonthPattern.test(dayOfMonth)) return ["ERR_INVALID_ADV_PARAM_OPT"];
  if (!dayOfWeekPattern.test(dayOfWeek)) return ["ERR_INVALID_ADV_PARAM_OPT"];
  if (!yearPattern.test(year)) return ["ERR_INVALID_ADV_PARAM_OPT"];
  if ((dayOfMonth === "?" && dayOfWeek === "?") || (dayOfMonth !== "?" && dayOfWeek !== "?")) {
    return ["ERR_INVALID_ADV_PARAM_OPT"];
  }
  return [];
}

function defaultForm(workflowGroups) {
  return {
    scheduleName: "",
    wfGroup: workflowGroups?.[0] || "",
    status: "disabled",
    misfireInstruction: "ON_MISFIRE_EXECUTE_AS_SOON_AS_POSSIBLE",
    runOnServer: "",
    startDate: "",
    endDate: "",
    timeFrom: "",
    timeTo: "",
    runConcurrent: false,
    priority: "5",
    runTimeSpan: "",
    intervalUnit: "DAY",
    intervalQty: "1",
    includeDays: [],
    monthRadio: "month1",
    dayOfMonth: "1",
    weekOfMonth: "1",
    dayOfWeek: "1",
    yearRadio: "year1",
    monthOfYear: "1",
    cronExprBuilder: {
      seconds: "0",
      minutes: "0",
      hours: "0",
      dayOfMonth: "*",
      dayOfWeek: "?",
      month: "*",
      year: "",
    },
    workflows: [],
    calendars: [],
  };
}

function formFromRow(row, workflowGroups) {
  return {
    ...defaultForm(workflowGroups),
    scheduleName: row.schedule_name || "",
    wfGroup: row.workflow_group || "",
    status: row.status === "enabled" ? "enabled" : "disabled",
    workflows: Array.isArray(row.workflows) ? [...row.workflows] : [],
    calendars: Array.isArray(row.calendars) ? [...row.calendars] : [],
  };
}

function mergeScheduleDetails(baseForm, details) {
  const normalizeRadioValue = (value, prefix, fallback) => {
    if (value === undefined || value === null) return fallback;
    const raw = String(value).trim().toLowerCase();
    if (!raw) return fallback;
    if (raw === `${prefix}1` || raw === "1") return `${prefix}1`;
    if (raw === `${prefix}2` || raw === "2") return `${prefix}2`;
    return fallback;
  };

  const parseCronString = (value) => {
    const text = String(value || "").trim();
    if (!text) return null;
    const parts = text.split(/\s+/);
    if (parts.length < 6) return null;
    const [seconds = "0", minutes = "0", hours = "0", dayOfMonth = "*", month = "*", dayOfWeek = "?", year = ""] = parts;
    return { seconds, minutes, hours, dayOfMonth, month, dayOfWeek, year };
  };

  const normalizeDateForInput = (value) => {
    if (!value || typeof value !== "string") return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const mmddyyyy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mmddyyyy) {
      const [, mm, dd, yyyy] = mmddyyyy;
      return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    }
    return "";
  };
  const normalizeTimeForInput = (value) => {
    const text = String(value || "").trim();
    if (!text) return "";
    const parts = text.split(":");
    if (parts.length >= 2) {
      const hh = parts[0].padStart(2, "0");
      const mm = parts[1].padStart(2, "0");
      return `${hh}:${mm}`;
    }
    return text;
  };

  if (!details || typeof details !== "object") return baseForm;
  const scheduleObj = details?.SCHEDULE_DEFINITION?.SCHEDULE || details;
  const merged = { ...baseForm };
  merged.scheduleName = scheduleObj.NAME || scheduleObj.name || scheduleObj.schedule_name || merged.scheduleName;
  merged.wfGroup = scheduleObj.WF_GROUP || scheduleObj.wfGroup || scheduleObj.workflow_group || merged.wfGroup;
  if (typeof scheduleObj.ENABLED === "boolean") {
    merged.status = scheduleObj.ENABLED ? "enabled" : "disabled";
  } else if (typeof scheduleObj.enabled === "boolean") {
    merged.status = scheduleObj.enabled ? "enabled" : "disabled";
  } else if (typeof scheduleObj.status === "string") {
    merged.status = scheduleObj.status.toLowerCase().includes("enable") ? "enabled" : "disabled";
  }
  merged.misfireInstruction =
    scheduleObj.MISFIRE_INSTRUCTION || scheduleObj.misfireInstruction || merged.misfireInstruction;
  merged.startDate = normalizeDateForInput(
    scheduleObj.START_DATE || scheduleObj.startDateString || scheduleObj.startDate || merged.startDate
  );
  merged.endDate = normalizeDateForInput(
    scheduleObj.END_DATE || scheduleObj.endDateString || scheduleObj.endDate || merged.endDate
  );
  merged.timeFrom = normalizeTimeForInput(scheduleObj.TIME || scheduleObj.timeString || merged.timeFrom);
  merged.timeTo = normalizeTimeForInput(scheduleObj.END_TIME || scheduleObj.endTimeString || merged.timeTo);
  merged.runConcurrent =
    typeof scheduleObj.CONCURRENT === "boolean"
      ? scheduleObj.CONCURRENT
      : typeof scheduleObj.runConcurrent === "boolean"
        ? scheduleObj.runConcurrent
        : merged.runConcurrent;
  merged.priority = String(scheduleObj.PRIORITY ?? scheduleObj.priorityString ?? scheduleObj.priority ?? merged.priority);
  merged.runTimeSpan = normalizeTimeForInput(scheduleObj.RUN_TIME || scheduleObj.runTimeString || merged.runTimeSpan);
  merged.intervalUnit = scheduleObj.INTERVAL_UNIT || scheduleObj.intervalUnit || merged.intervalUnit;
  merged.intervalQty = String(scheduleObj.INTERVAL_QTY || scheduleObj.intervalQty || merged.intervalQty);
  merged.monthRadio = normalizeRadioValue(
    scheduleObj.MONTH_RADIO ?? scheduleObj.monthRadio,
    "month",
    merged.monthRadio
  );
  merged.dayOfMonth = String(scheduleObj.DAY_OF_MONTH || scheduleObj.dayOfMonth || merged.dayOfMonth);
  merged.weekOfMonth = String(scheduleObj.WEEK_OF_MONTH || scheduleObj.weekOfMonth || merged.weekOfMonth);
  merged.dayOfWeek = String(scheduleObj.DAY_OF_WEEK || scheduleObj.dayOfWeek || merged.dayOfWeek);
  merged.yearRadio = normalizeRadioValue(
    scheduleObj.YEAR_RADIO ?? scheduleObj.yearRadio,
    "year",
    merged.yearRadio
  );
  merged.monthOfYear = String(scheduleObj.MONTH_OF_YEAR || scheduleObj.monthOfYear || merged.monthOfYear);
  merged.runOnServer = scheduleObj.RUN_ON_SERVER || scheduleObj.runOnServer || merged.runOnServer;
  if (scheduleObj.cronExprBuilder && typeof scheduleObj.cronExprBuilder === "object") {
    merged.cronExprBuilder = {
      ...merged.cronExprBuilder,
      ...scheduleObj.cronExprBuilder,
    };
  }
  const cronString =
    scheduleObj.CRON_STRING ||
    scheduleObj.cronString ||
    scheduleObj.cron_expression ||
    scheduleObj.cronExpr ||
    "";
  const parsedCron = parseCronString(cronString);
  if (parsedCron) {
    merged.cronExprBuilder = {
      ...merged.cronExprBuilder,
      ...parsedCron,
    };
    if (!scheduleObj.INTERVAL_UNIT && !scheduleObj.intervalUnit) {
      merged.intervalUnit = "ADVANCED";
    }
  }
  if (Array.isArray(scheduleObj.WORKFLOW_KEY)) {
    const resolvedWorkflows = scheduleObj.WORKFLOW_KEY.map((w) => {
      if (w?.WF_GROUP && w?.WF_NAME) return `${w.WF_GROUP}/${w.WF_NAME}`;
      return "";
    }).filter(Boolean);
    if (resolvedWorkflows.length) {
      merged.workflows = resolvedWorkflows;
    }
  } else if (Array.isArray(scheduleObj.workflows)) {
    const resolvedWorkflows = scheduleObj.workflows.map((w) => {
      if (typeof w === "string") return w;
      if (w?.workflowGroup && w?.workflowName) return `${w.workflowGroup}/${w.workflowName}`;
      return "";
    }).filter(Boolean);
    if (resolvedWorkflows.length) {
      merged.workflows = resolvedWorkflows;
    }
  }
  if (Array.isArray(scheduleObj.CALENDAR)) {
    const resolvedCalendars = scheduleObj.CALENDAR
      .map((c) => {
        if (typeof c === "string") return c;
        if (c && typeof c === "object") return c.NAME || c.name || "";
        return "";
      })
      .filter(Boolean);
    if (resolvedCalendars.length) {
      merged.calendars = resolvedCalendars;
    }
  } else if (Array.isArray(scheduleObj.calendarNames)) {
    const resolvedCalendars = scheduleObj.calendarNames.filter(Boolean);
    if (resolvedCalendars.length) {
      merged.calendars = [...resolvedCalendars];
    }
  }

  return merged;
}

function Section({ title, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
      <Typography
        variant="overline"
        sx={{ display: "block", mb: 2, color: "text.secondary", letterSpacing: 1 }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function groupWorkflowItems(items) {
  const grouped = {};
  items.forEach((wf) => {
    const [group, ...nameParts] = String(wf).split("/");
    const wfName = nameParts.join("/");
    if (!group || !wfName) return;
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(wfName);
  });
  Object.keys(grouped).forEach((k) => grouped[k].sort((a, b) => a.localeCompare(b)));
  return grouped;
}

function SimpleTreePanel({
  title,
  groupLabel,
  groupedItems,
  iconType,
  onToggleItem,
  onToggleGroup,
  loading = false,
}) {
  const [expandedGroups, setExpandedGroups] = useState({});
  const groups = Object.keys(groupedItems);

  useEffect(() => {
    // Default all folders collapsed when panel data changes.
    const next = {};
    const autoExpandSingleGroup = groups.length === 1;
    groups.forEach((g) => {
      next[g] = autoExpandSingleGroup;
    });
    setExpandedGroups(next);
  }, [groups.join("|")]);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1, borderColor: "#cfd5df", overflow: "hidden", bgcolor: "#fff" }}>
      <Box sx={{ px: 1.25, py: 0.85, borderBottom: "1px solid #dde2ea", bgcolor: "#edf0f5" }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#616a79", textTransform: "uppercase", letterSpacing: 0.3 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ height: 260, maxHeight: 260, overflow: "auto", p: 0.75 }}>
        {loading ? (
          <Box sx={{ height: "100%", minHeight: 160, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={20} />
          </Box>
        ) : null}
        {!loading ? Object.entries(groupedItems).map(([group, names]) => (
          <Box key={group} sx={{ mb: 0.45 }}>
            <Box
              sx={{
                px: 0.4,
                minHeight: 26,
                display: "grid",
                gridTemplateColumns: "1fr",
                alignItems: "center",
                columnGap: 0.2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.4,
                  color: "#5b6473",
                  userSelect: "none",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Box sx={{ width: 18, display: "flex", justifyContent: "center", alignItems: "center", flexShrink: 0 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }))}
                    title={expandedGroups[group] ? "Collapse folder" : "Expand folder"}
                    sx={{
                      p: 0,
                      m: 0,
                      border: 0,
                      background: "transparent",
                      color: "#7b8493",
                      cursor: "pointer",
                      lineHeight: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {expandedGroups[group] ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
                  </Box>
                </Box>
                <Box sx={{ width: 16, display: "flex", justifyContent: "center", alignItems: "center", flexShrink: 0 }}>
                  <FolderOpenIcon sx={{ fontSize: 16, color: "#7b8493" }} />
                </Box>
                <Typography
                  variant="caption"
                  onClick={() => setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }))}
                  sx={{ fontWeight: 700, lineHeight: 1.2, cursor: "pointer", flex: 1, minWidth: 0 }}
                >
                  {groupLabel || group}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  onClick={() => onToggleGroup(group, names)}
                  title={iconType === "add" ? "Add entire folder" : "Remove entire folder"}
                  sx={{
                    p: 0,
                    m: 0,
                    border: 0,
                    background: "transparent",
                    color: "#69707d",
                    cursor: "pointer",
                    lineHeight: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {iconType === "add" ? <AddCircleOutlineIcon sx={{ fontSize: 18 }} /> : <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                mt: 0.15,
                pl: 1.2,
                display: expandedGroups[group] ? "block" : "none",
                width: "100%",
              }}
            >
              {names.map((name) => (
                <Box
                  key={`${group}/${name}`}
                  sx={{
                    width: "88%",
                    mx: "auto",
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    alignItems: "center",
                    columnGap: 0.2,
                    color: "#4f5867",
                    px: 0.6,
                    py: 0.2,
                    minHeight: 26,
                    borderRadius: 0.6,
                    "&:hover": { bgcolor: "#f7f9fc" },
                  }}
                >
                  <Stack direction="row" spacing={0.6} alignItems="center" sx={{ width: "100%" }}>
                    <Box sx={{ width: 16, display: "flex", justifyContent: "center", alignItems: "center", flexShrink: 0 }}>
                      <SettingsIcon sx={{ fontSize: 14, color: "#8a93a2" }} />
                    </Box>
                    <Typography variant="body2" sx={{ textAlign: "left", fontSize: "0.82rem", flex: 1, minWidth: 0 }}>
                      {name}
                    </Typography>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onToggleItem(`${group}/${name}`)}
                      title={iconType === "add" ? "Add item" : "Remove item"}
                      sx={{
                        p: 0,
                        m: 0,
                        border: 0,
                        background: "transparent",
                        color: "#69707d",
                        cursor: "pointer",
                        lineHeight: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {iconType === "add" ? (
                        <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                      )}
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Box>
          </Box>
        )) : null}
      </Box>
    </Paper>
  );
}

export default function ScheduleEditorFullScreen({
  open,
  onClose,
  onSaved,
  isNew,
  row,
  account,
  baseAccount,
  user,
  accounts = [],
  workflowGroups = [],
  assignableWorkflows = [],
  calendarNames = [],
  existingSchedules = [],
}) {
  const { t } = useTranslation();
  const multiAccountMode = baseAccount && baseAccount.toUpperCase && baseAccount.toUpperCase() === "ALL";
  const [scheduleAccount, setScheduleAccount] = useState(account || "");

  const { data: serverPayload } = useGetServerCommonNameListQuery(
    { user, account: scheduleAccount || account },
    { skip: !open }
  );
  const servers = useMemo(() => {
    const ok = serverPayload?.current_state === "SUCCESS" || serverPayload?.status === "SUCCESS";
    if (!ok) return [];
    if (Array.isArray(serverPayload?.data)) return serverPayload.data;
    if (serverPayload?.data && typeof serverPayload.data === "object") {
      const vals = Object.values(serverPayload.data).filter((v) => typeof v === "string" && v.trim());
      return vals;
    }
    return [];
  }, [serverPayload]);
  const runServerOptions = useMemo(() => {
    const defaults = [
      "LBL_RUN_ON_ALL_ACTIVE_SERVER",
      "LBL_RUN_ON_ANY_SERVER",
      "LBL_RUN_ON_PRIMARY",
    ];
    return Array.from(new Set([...defaults, ...servers.filter(Boolean)]));
  }, [servers]);

  const workflowsQuery = useGetWorkflowsCopyQuery(
    { account: scheduleAccount || account, user },
    { skip: !open || !(scheduleAccount || account) }
  );

  const workflowTreeSource = useMemo(() => {
    if (
      workflowsQuery.data?.current_state === "SUCCESS" &&
      Array.isArray(workflowsQuery.data?.data?.workflow_groups)
    ) {
      const list = [];
      workflowsQuery.data.data.workflow_groups.forEach((group) => {
        const gName = group?.name;
        if (!gName || !Array.isArray(group.workflows)) return;
        group.workflows.forEach((wf) => {
          if (wf?.type === "WORKFLOW" && wf?.name) {
            list.push(`${gName}/${wf.name}`);
          }
        });
      });
      if (list.length) return list;
    }
    if (assignableWorkflows?.length) return assignableWorkflows;
    return [
      "sample1/wf-order-import",
      "sample1/wf-order-export",
      "sample1/wf-ack-processor",
      "samples/wf-invoice-map",
      "samples/wf-shipping-update",
    ];
  }, [workflowsQuery.data, assignableWorkflows, scheduleAccount, account]);
  const calendarTreeSource = useMemo(
    () => (Array.isArray(calendarNames) ? calendarNames : []),
    [calendarNames]
  );

  const [form, setForm] = useState(() => defaultForm(workflowGroups));
  const [loadScheduleDetails] = useLazyGetScheduleDetailsQuery();
  const [saveScheduleDetails] = useSaveScheduleDetailsMutation();
  const [saveTemplates] = useSaveTemplatesMutation();
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [loadedTemplate, setLoadedTemplate] = useState(null);
  const drawerPaperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (isNew) {
      setForm(defaultForm(workflowGroups));
    } else if (row) {
      // Initialize edit form when drawer opens or target row changes.
      // Do not reset on reference-data refreshes to avoid clearing loaded assignments.
      setForm(formFromRow(row, workflowGroups));
    }
  }, [open, isNew, row]);

  useEffect(() => {
    if (!open || !isNew || !workflowGroups.length) return;
    setForm((f) => (f.wfGroup ? f : { ...f, wfGroup: workflowGroups[0] }));
  }, [open, isNew, workflowGroups]);

  useEffect(() => {
    if (!open || isNew || !row?.schedule_name || !(scheduleAccount || account)) return;
    loadScheduleDetails({
      user,
      account: scheduleAccount || account,
      workflow_group_name: row.workflow_group,
      schedule_name: row.schedule_name,
    }).then((res) => {
      const details = res?.data?.data || res?.data || null;
      if (details) {
        setLoadedTemplate(details);
        setForm((f) => mergeScheduleDetails(f, details));
      }
    });
  }, [open, isNew, row, scheduleAccount, account, user, loadScheduleDetails]);

  useEffect(() => {
    if (!open || runServerOptions.length === 0 || form.runOnServer) return;
    setForm((f) => (f.runOnServer ? f : { ...f, runOnServer: runServerOptions[0] }));
  }, [open, runServerOptions, form.runOnServer]);

  useEffect(() => {
    if (!open || !multiAccountMode) return;
    if (!scheduleAccount) {
      if (account) {
        setScheduleAccount(account);
      } else if (accounts.length) {
        setScheduleAccount(accounts[0]);
      }
    }
  }, [open, multiAccountMode, scheduleAccount, account, accounts]);

  useEffect(() => {
    if (!isSaving) return;
    const paperEl = drawerPaperRef.current;
    if (paperEl && typeof paperEl.scrollTo === "function") {
      paperEl.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [isSaving]);

  const setField = (key) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value ?? e;
    setForm((f) => ({ ...f, [key]: v }));
  };
  const sanitizeTimeInput = (value) => {
    const cleaned = String(value || "").replace(/[^\d:]/g, "");
    if (!cleaned) return "";
    if (cleaned.includes(":")) {
      const [h = "", m = ""] = cleaned.split(":");
      return `${h.slice(0, 2)}:${m.slice(0, 2)}`;
    }
    const digits = cleaned.slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  };
  const setTimeField = (key) => (e) => {
    const v = sanitizeTimeInput(e?.target?.value ?? "");
    setForm((f) => ({ ...f, [key]: v }));
  };
  const setCronField = (key) => (e) => {
    const v = e?.target?.value ?? "";
    setForm((f) => ({
      ...f,
      cronExprBuilder: {
        ...f.cronExprBuilder,
        [key]: v,
      },
    }));
  };
  const toggleWeekday = (day) => {
    setForm((f) => {
      const has = f.includeDays.includes(day);
      return {
        ...f,
        includeDays: has ? f.includeDays.filter((d) => d !== day) : [...f.includeDays, day].sort((a, b) => a - b),
      };
    });
  };

  const title = isNew ? "New schedule" : "Edit schedule";
  const displayAccount = multiAccountMode ? scheduleAccount : account;
  const invalidScheduleNameError = useMemo(() => {
    const name = String(form.scheduleName || "").trim();
    if (!name) return "";
    return /[\\^$.|?*+()]/.test(name)
      ? "Schedule names cannot contain: \\ ^ $ . | ? * + ( )"
      : "";
  }, [form.scheduleName]);
  const duplicateScheduleNameError = useMemo(() => {
    if (!isNew) return "";
    const scheduleName = String(form.scheduleName || "").trim().toLowerCase();
    const wfGroup = String(form.wfGroup || "").trim().toLowerCase();
    if (!scheduleName || !wfGroup) return "";
    const duplicate = existingSchedules.some((s) => {
      const existingName = String(s?.schedule_name || s?.name || "").trim().toLowerCase();
      const existingGroup = String(s?.workflow_group || s?.wfGroup || "").trim().toLowerCase();
      return existingName === scheduleName && existingGroup === wfGroup;
    });
    return duplicate ? "Schedule name already exists in selected workflow group." : "";
  }, [isNew, form.scheduleName, form.wfGroup, existingSchedules]);
  const advancedErrors = useMemo(
    () => (form.intervalUnit === "ADVANCED" ? getAdvancedValidationErrors(form.cronExprBuilder) : []),
    [form.intervalUnit, form.cronExprBuilder]
  );
  const canSave =
    !!displayAccount &&
    !!form.scheduleName?.trim() &&
    !!form.wfGroup?.trim() &&
    !!form.misfireInstruction &&
    !!form.runOnServer &&
    !invalidScheduleNameError &&
    !duplicateScheduleNameError &&
    advancedErrors.length === 0;

  const toMMDDYYYY = (dateValue) => {
    if (!dateValue) return "";
    const m = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return dateValue;
    return `${m[2]}/${m[3]}/${m[1]}`;
  };

  const buildSavePayload = () => {
    const workflowKeys = (form.workflows || []).map((wf) => {
      const [wfGroup, ...wfNameParts] = String(wf).split("/");
      return {
        WF_GROUP: wfGroup || "",
        WF_NAME: wfNameParts.join("/"),
      };
    }).filter((w) => w.WF_GROUP && w.WF_NAME);

    const baseRoot =
      loadedTemplate?.SCHEDULE_DEFINITION && typeof loadedTemplate.SCHEDULE_DEFINITION === "object"
        ? loadedTemplate
        : {
            SCHEDULE_DEFINITION: {
              BUILD: "",
              LAST_MODIFIED_USER: user || "",
              REVISION_ID: "1",
              VERSION: "",
            },
          };

    const isAdvanced = form.intervalUnit === "ADVANCED";
    const cronString = isAdvanced
      ? `${form.cronExprBuilder.seconds || "*"} ${form.cronExprBuilder.minutes || "*"} ${form.cronExprBuilder.hours || "*"} ${form.cronExprBuilder.dayOfMonth || "?"} ${form.cronExprBuilder.month || "*"} ${form.cronExprBuilder.dayOfWeek || "?"} ${form.cronExprBuilder.year || ""}`.trim()
      : "";

    const schedule = {
      NAME: form.scheduleName,
      WF_GROUP: form.wfGroup,
      ENABLED: form.status === "enabled",
      MISFIRE_INSTRUCTION: form.misfireInstruction,
      START_DATE: toMMDDYYYY(form.startDate),
      END_DATE: toMMDDYYYY(form.endDate),
      TIME: form.timeFrom || "",
      END_TIME: form.timeTo || "",
      CONCURRENT: !!form.runConcurrent,
      PRIORITY: Number(form.priority || 0),
      RUN_TIME: form.runTimeSpan || "",
      INTERVAL_UNIT: form.intervalUnit,
      INTERVAL_QTY: Number(form.intervalQty || 1),
      RUN_ON_SERVER: form.runOnServer || "",
      CALENDAR: (form.calendars || []).map((c) => ({ NAME: c })),
      WORKFLOW_KEY: workflowKeys,
      MONTH_RADIO: form.monthRadio,
      YEAR_RADIO: form.yearRadio,
      DAY_OF_MONTH: Number(form.dayOfMonth || 1),
      WEEK_OF_MONTH: Number(form.weekOfMonth || 1),
      DAY_OF_WEEK: Number(form.dayOfWeek || 1),
      MONTH_OF_YEAR: Number(form.monthOfYear || 1),
      INCLUDE_DAYS: Array.isArray(form.includeDays) ? form.includeDays.join(",") : "",
      CRON_STRING: cronString,
      REAL_TIME: false,
      CONCURRENT: !!form.runConcurrent,
    };

    return {
      ...baseRoot,
      SCHEDULE_DEFINITION: {
        ...baseRoot.SCHEDULE_DEFINITION,
        LAST_MODIFIED_USER: user || baseRoot?.SCHEDULE_DEFINITION?.LAST_MODIFIED_USER || "",
        SCHEDULE: schedule,
      },
    };
  };

  const handleReset = () => {
    setSaveError("");
    if (isNew) {
      const next = defaultForm(workflowGroups);
      if (runServerOptions.length && !next.runOnServer) {
        next.runOnServer = runServerOptions[0];
      }
      setForm(next);
      return;
    }

    let next = formFromRow(row || {}, workflowGroups);
    if (loadedTemplate) {
      next = mergeScheduleDetails(next, loadedTemplate);
    }
    if (runServerOptions.length && !next.runOnServer) {
      next.runOnServer = runServerOptions[0];
    }
    setForm(next);
  };

  const handleSave = async () => {
    if (!canSave || isSaving) return;
    setSaveError("");
    setIsSaving(true);
    try {
      const payload = JSON.stringify(buildSavePayload());

      const commonArgs = {
        user,
        account: displayAccount,
        workflow_group_name: form.wfGroup,
        schedule_name: form.scheduleName,
        data: payload,
      };

      const unsavedRes = await saveScheduleDetails({
        ...commonArgs,
        store_unsaved_changes: true,
      });
      const unsavedStateRaw = unsavedRes?.data?.current_state || unsavedRes?.data?.status;
      const unsavedState = typeof unsavedStateRaw === "string" ? unsavedStateRaw.toUpperCase() : "";
      if (unsavedRes?.error || unsavedState !== "SUCCESS") {
        setSaveError(
          unsavedRes?.error?.data?.message ||
          unsavedRes?.error?.error ||
          unsavedRes?.data?.message ||
          "Saving unsaved schedule draft failed."
        );
        return;
      }

      const saveTemplatesPayload = JSON.stringify({
        schedules: [
          {
            account: displayAccount,
            workflow_group: form.wfGroup,
            template: form.scheduleName,
          },
        ],
      });

      const res = await saveTemplates({
        save_to_server: true,
        data: saveTemplatesPayload,
      });
      const stateRaw = res?.data?.current_state || res?.data?.status || res?.data?.state;
      const state = typeof stateRaw === "string" ? stateRaw.toUpperCase() : "";
      if (res?.error || state !== "SUCCESS") {
        setSaveError(
          res?.error?.data?.message ||
          res?.error?.error ||
          res?.data?.message ||
          "Save templates failed."
        );
        return;
      }
      if (onSaved) onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={isSaving ? undefined : onClose}
      PaperProps={{
        ref: drawerPaperRef,
        sx: {
          width: "94vw",
          maxWidth: 1320,
          bgcolor: "background.default",
          position: "relative",
          overflowY: isSaving ? "hidden" : "auto",
        },
      }}
    >
      <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Toolbar>
          <Typography sx={{ flex: 1, fontSize: "1.2rem", fontWeight: 800, color: "#5f6878", textTransform: "uppercase", letterSpacing: 0.2 }}>
            {title}
          </Typography>
          {displayAccount ? (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
              Account: <strong>{displayAccount}</strong>
            </Typography>
          ) : null}
          <Button variant="outlined" onClick={onClose} sx={{ mr: 1 }} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={handleReset} sx={{ mr: 1 }} disabled={isSaving}>
            Reset
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} disabled={!canSave || isSaving} onClick={handleSave}>
            Save
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          maxWidth: 1200,
          mx: "auto",
          width: "100%",
          boxSizing: "border-box",
          pb: 6,
        }}
      >
        {saveError ? <Alert severity="error" sx={{ mb: 1 }}>{saveError}</Alert> : null}
        <Typography variant="caption" sx={{ color: "#7a8290", mb: 1, display: "block" }}>
          Please note: * denotes required fields
        </Typography>

        <Section title="Schedule Information">
          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              {multiAccountMode ? (
                <FormControl size="small" required sx={{ gridColumn: { md: "1 / -1" } }}>
                  <InputLabel>*Account</InputLabel>
                  <Select
                    label="*Account"
                    value={scheduleAccount}
                    onChange={(e) => setScheduleAccount(e.target.value)}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: 320 },
                      },
                    }}
                  >
                    {accounts.map((acc) => (
                      <MenuItem key={acc} value={acc}>
                        {acc}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              <TextField
                required
                label="*New Schedule Name"
                value={form.scheduleName}
                onChange={setField("scheduleName")}
                disabled={!isNew}
                size="small"
                error={!!(invalidScheduleNameError || duplicateScheduleNameError)}
                helperText={invalidScheduleNameError || duplicateScheduleNameError || ""}
              />
              {isNew ? (
                <FormControl size="small" required>
                  <InputLabel>*WF Group Placement</InputLabel>
                  <Select label="*WF Group Placement" value={form.wfGroup} onChange={setField("wfGroup")}>
                    {workflowGroups.map((g) => (
                      <MenuItem key={g} value={g}>
                        {g}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField label="*WF Group Placement" value={form.wfGroup} size="small" disabled fullWidth />
              )}
              <FormControl size="small" required>
                <InputLabel>*Status</InputLabel>
                <Select label="*Status" value={form.status} onChange={setField("status")}>
                  <MenuItem value="enabled">Enabled</MenuItem>
                  <MenuItem value="disabled">Disabled</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" required>
                <InputLabel>*Scheduler Misfire Instruction</InputLabel>
                <Select label="*Scheduler Misfire Instruction" value={form.misfireInstruction} onChange={setField("misfireInstruction")}>
                  {MISFIRE_SIMPLE.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" required sx={{ gridColumn: { md: "1 / -1" } }}>
                <InputLabel>*Run on Server</InputLabel>
                <Select label="*Run on Server" value={form.runOnServer} onChange={setField("runOnServer")}>
                  {runServerOptions.map((s) => (
                    <MenuItem key={s} value={s}>
                      {runServerDisplayText(s)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Section>

        <Section title="Criteria">
          <Stack spacing={2}>
            <Typography variant="caption" sx={{ color: "#7a8290" }}>
              Schedule Active Duration
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                gap: 2,
              }}
            >
              <TextField label="From Date (yyyy/mm/dd)" type="date" value={form.startDate} onChange={setField("startDate")} size="small" InputLabelProps={{ shrink: true }} />
              <TextField
                label="From Time 24 hour (hh:mm)"
                placeholder="HH:mm"
                value={form.timeFrom}
                onChange={setTimeField("timeFrom")}
                size="small"
                inputProps={{ inputMode: "numeric", maxLength: 5 }}
              />
              <TextField label="To Date (yyyy/mm/dd)" type="date" value={form.endDate} onChange={setField("endDate")} size="small" InputLabelProps={{ shrink: true }} />
              <TextField
                label="To Time 24 hour (hh:mm)"
                placeholder="HH:mm"
                value={form.timeTo}
                onChange={setTimeField("timeTo")}
                size="small"
                inputProps={{ inputMode: "numeric", maxLength: 5 }}
              />
            </Box>
            <Divider />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr" },
                gap: 2,
                alignItems: "start",
              }}
            >
              <FormControl size="small">
                <InputLabel>*When Scheduled times overlap</InputLabel>
                <Select
                  label="*When Scheduled times overlap"
                  value={form.runConcurrent ? "concurrent" : "sequential"}
                  onChange={(e) => setForm((f) => ({ ...f, runConcurrent: e.target.value === "concurrent" }))}
                >
                  <MenuItem value="sequential">Run sequentially</MenuItem>
                  <MenuItem value="concurrent">Run concurrently</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Priority"
                value={form.priority}
                onChange={setField("priority")}
                size="small"
                inputProps={{ maxLength: 2 }}
              />
            </Box>
            <Divider />
            <Stack spacing={2}>
            <FormControl component="fieldset" variant="standard">
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                *Recurrence
              </Typography>
              <RadioGroup
                row
                value={form.intervalUnit}
                onChange={setField("intervalUnit")}
                sx={{ flexWrap: "wrap", gap: 0.5 }}
              >
                {INTERVAL_UNITS.map((u) => (
                  <FormControlLabel key={u.value} value={u.value} control={<Radio size="small" />} label={u.label} />
                ))}
              </RadioGroup>
            </FormControl>
            {["DAY", "WEEK", "MONTH", "YEAR", "ADVANCED"].includes(form.intervalUnit) ? (
              <TextField
                label="Execute Time 24 hour (hh:mm)"
                value={form.runTimeSpan}
                onChange={setTimeField("runTimeSpan")}
                size="small"
                sx={{ maxWidth: 360 }}
                inputProps={{ inputMode: "numeric", maxLength: 5 }}
              />
            ) : null}
            {form.intervalUnit !== "ADVANCED" ? (
              <Box>
                {(form.intervalUnit === "SECOND" ||
                  form.intervalUnit === "MINUTE" ||
                  form.intervalUnit === "HOUR" ||
                  form.intervalUnit === "DAY") && (
                  <TextField
                    label="Run Every"
                    value={form.intervalQty}
                    onChange={setField("intervalQty")}
                    size="small"
                    sx={{ maxWidth: 220 }}
                  />
                )}

                {form.intervalUnit === "WEEK" && (
                  <Stack spacing={1.5}>
                    <TextField
                      label="Run Every (Weeks)"
                      value={form.intervalQty}
                      onChange={setField("intervalQty")}
                      size="small"
                      sx={{ maxWidth: 220 }}
                    />
                    <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
                      {DAY_OPTIONS.map((day) => (
                        <FormControlLabel
                          key={day.value}
                          control={
                            <Checkbox
                              size="small"
                              checked={form.includeDays.includes(day.value)}
                              onChange={() => toggleWeekday(day.value)}
                            />
                          }
                          label={day.label}
                        />
                      ))}
                    </Stack>
                  </Stack>
                )}

                {form.intervalUnit === "MONTH" && (
                  <Stack spacing={1.5}>
                    <TextField
                      label="Run Every (Months)"
                      value={form.intervalQty}
                      onChange={setField("intervalQty")}
                      size="small"
                      sx={{ maxWidth: 220 }}
                    />
                    <RadioGroup
                      value={form.monthRadio}
                      onChange={setField("monthRadio")}
                      sx={{ gap: 1 }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap", minHeight: 40 }}>
                        <FormControlLabel value="month1" control={<Radio size="small" />} label="Day Of Month" />
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Day</InputLabel>
                          <Select
                            label="Day"
                            value={form.dayOfMonth}
                            onChange={setField("dayOfMonth")}
                            disabled={form.monthRadio !== "month1"}
                          >
                            {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => (
                              <MenuItem key={d} value={d}>{d}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap", minHeight: 40 }}>
                        <FormControlLabel value="month2" control={<Radio size="small" />} label="Week / Day" />
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                          <InputLabel>Week</InputLabel>
                          <Select
                            label="Week"
                            value={form.weekOfMonth}
                            onChange={setField("weekOfMonth")}
                            disabled={form.monthRadio !== "month2"}
                          >
                            <MenuItem value="1">First</MenuItem>
                            <MenuItem value="2">Second</MenuItem>
                            <MenuItem value="3">Third</MenuItem>
                            <MenuItem value="4">Fourth</MenuItem>
                            <MenuItem value="5">Fifth</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                          <InputLabel>Day</InputLabel>
                          <Select
                            label="Day"
                            value={form.dayOfWeek}
                            onChange={setField("dayOfWeek")}
                            disabled={form.monthRadio !== "month2"}
                          >
                            {DAY_OPTIONS.map((d) => (
                              <MenuItem key={d.value} value={String(d.value)}>{d.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    </RadioGroup>
                  </Stack>
                )}

                {form.intervalUnit === "YEAR" && (
                  <Stack spacing={1.5}>
                    <RadioGroup
                      value={form.yearRadio}
                      onChange={setField("yearRadio")}
                      sx={{ gap: 1 }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap", minHeight: 40 }}>
                        <FormControlLabel value="year1" control={<Radio size="small" />} label="Month / Day" />
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <InputLabel>Month</InputLabel>
                          <Select
                            label="Month"
                            value={form.monthOfYear}
                            onChange={setField("monthOfYear")}
                            disabled={form.yearRadio !== "year1"}
                          >
                            {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m, idx) => (
                              <MenuItem key={m} value={String(idx + 1)}>{m}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 110 }}>
                          <InputLabel>Day</InputLabel>
                          <Select
                            label="Day"
                            value={form.dayOfMonth}
                            onChange={setField("dayOfMonth")}
                            disabled={form.yearRadio !== "year1"}
                          >
                            {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => (
                              <MenuItem key={d} value={d}>{d}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap", minHeight: 40 }}>
                        <FormControlLabel value="year2" control={<Radio size="small" />} label="Month / Week / Day" />
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <InputLabel>Month</InputLabel>
                          <Select
                            label="Month"
                            value={form.monthOfYear}
                            onChange={setField("monthOfYear")}
                            disabled={form.yearRadio !== "year2"}
                          >
                            {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m, idx) => (
                              <MenuItem key={m} value={String(idx + 1)}>{m}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                          <InputLabel>Week</InputLabel>
                          <Select
                            label="Week"
                            value={form.weekOfMonth}
                            onChange={setField("weekOfMonth")}
                            disabled={form.yearRadio !== "year2"}
                          >
                            <MenuItem value="1">First</MenuItem>
                            <MenuItem value="2">Second</MenuItem>
                            <MenuItem value="3">Third</MenuItem>
                            <MenuItem value="4">Fourth</MenuItem>
                            <MenuItem value="5">Fifth</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                          <InputLabel>Day</InputLabel>
                          <Select
                            label="Day"
                            value={form.dayOfWeek}
                            onChange={setField("dayOfWeek")}
                            disabled={form.yearRadio !== "year2"}
                          >
                            {DAY_OPTIONS.map((d) => (
                              <MenuItem key={d.value} value={String(d.value)}>{d.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    </RadioGroup>
                  </Stack>
                )}
              </Box>
            ) : (
              <Box sx={{ border: "1px solid #d4d9e2", bgcolor: "#f7f8fb", p: 1.2, maxWidth: 760 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 2, rowGap: 0.75 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ minWidth: 70 }}>Seconds:</Typography>
                    <TextField value={form.cronExprBuilder.seconds} onChange={setCronField("seconds")} size="small" sx={{ width: 80 }} />
                    <Typography variant="caption">0-59 , - * /</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ minWidth: 90 }}>Day of Month:</Typography>
                    <TextField value={form.cronExprBuilder.dayOfMonth} onChange={setCronField("dayOfMonth")} size="small" sx={{ width: 80 }} />
                    <Typography variant="caption">1-31 , - * / ? L W</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ minWidth: 70 }}>Minutes:</Typography>
                    <TextField value={form.cronExprBuilder.minutes} onChange={setCronField("minutes")} size="small" sx={{ width: 80 }} />
                    <Typography variant="caption">0-59 , - * /</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ minWidth: 90 }}>Day of Week:</Typography>
                    <TextField value={form.cronExprBuilder.dayOfWeek} onChange={setCronField("dayOfWeek")} size="small" sx={{ width: 80 }} />
                    <Typography variant="caption">1-7 or SUN-SAT , - * / ? L #</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ minWidth: 70 }}>Hours:</Typography>
                    <TextField value={form.cronExprBuilder.hours} onChange={setCronField("hours")} size="small" sx={{ width: 80 }} />
                    <Typography variant="caption">0-23 , - * /</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ minWidth: 90 }}>Year (optional):</Typography>
                    <TextField value={form.cronExprBuilder.year} onChange={setCronField("year")} size="small" sx={{ width: 80 }} />
                    <Typography variant="caption">empty, 1970-2099 , - * /</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ minWidth: 70 }}>Month:</Typography>
                    <TextField value={form.cronExprBuilder.month} onChange={setCronField("month")} size="small" sx={{ width: 80 }} />
                    <Typography variant="caption">1-12 or JAN-DEC , - * /</Typography>
                  </Stack>
                </Box>
              </Box>
            )}
            {advancedErrors.length > 0 ? (
              <Alert severity="error" sx={{ py: 0 }}>
                {t("ERR_INVALID_ADV_PARAM_OPT")}
              </Alert>
            ) : null}
            </Stack>
          </Stack>
        </Section>

        <Section title="Workflows / Calendars">
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 700, color: "#5d6675", mb: 1 }}>Workflows</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <SimpleTreePanel
                  title="Unassigned WorkFlows"
                  groupedItems={groupWorkflowItems(workflowTreeSource.filter((wf) => !form.workflows.includes(wf)))}
                  iconType="add"
                  loading={workflowsQuery.isLoading || workflowsQuery.isFetching}
                  onToggleItem={(wf) => setForm((f) => ({ ...f, workflows: [...f.workflows, wf].sort((a, b) => a.localeCompare(b)) }))}
                  onToggleGroup={(group, names) => {
                    const full = names.map((n) => `${group}/${n}`);
                    setForm((f) => ({
                      ...f,
                      workflows: Array.from(new Set([...f.workflows, ...full])).sort((a, b) => a.localeCompare(b)),
                    }));
                  }}
                />
                <SimpleTreePanel
                  title="Assigned WorkFlows"
                  groupedItems={groupWorkflowItems(form.workflows)}
                  iconType="remove"
                  onToggleItem={(wf) => setForm((f) => ({ ...f, workflows: f.workflows.filter((x) => x !== wf) }))}
                  onToggleGroup={(group, names) => {
                    const full = new Set(names.map((n) => `${group}/${n}`));
                    setForm((f) => ({ ...f, workflows: f.workflows.filter((x) => !full.has(x)) }));
                  }}
                />
              </Box>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, color: "#5d6675", mb: 1 }}>Calendars</Typography>
              {!calendarTreeSource.length ? (
                <Typography variant="caption" sx={{ color: "#8b93a3", display: "block", mb: 1 }}>
                  No calendars returned from API for this account.
                </Typography>
              ) : null}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <SimpleTreePanel
                  title="Unassigned Calendars"
                  groupLabel="Calendars"
                  groupedItems={{ Calendars: calendarTreeSource.filter((c) => !form.calendars.includes(c)) }}
                  iconType="add"
                  onToggleItem={(val) => {
                    const cal = val.replace("Calendars/", "");
                    setForm((f) => ({ ...f, calendars: [...f.calendars, cal].sort((a, b) => a.localeCompare(b)) }));
                  }}
                  onToggleGroup={(_, names) => {
                    setForm((f) => ({
                      ...f,
                      calendars: Array.from(new Set([...f.calendars, ...names])).sort((a, b) => a.localeCompare(b)),
                    }));
                  }}
                />
                <SimpleTreePanel
                  title="Assigned Calendars"
                  groupLabel="Calendars"
                  groupedItems={{ Calendars: form.calendars }}
                  iconType="remove"
                  onToggleItem={(val) => {
                    const cal = val.replace("Calendars/", "");
                    setForm((f) => ({ ...f, calendars: f.calendars.filter((x) => x !== cal) }));
                  }}
                  onToggleGroup={(_, names) => {
                    const all = new Set(names);
                    setForm((f) => ({ ...f, calendars: f.calendars.filter((x) => !all.has(x)) }));
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Section>
      </Box>
      {isSaving ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255, 255, 255, 0.35)",
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
          }}
        >
          <Stack spacing={1.25} alignItems="center" sx={{ color: "#4f5867" }}>
            <CircularProgress size={28} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Saving schedule...
            </Typography>
          </Stack>
        </Box>
      ) : null}
    </Drawer>
  );
}
